import { memo, useCallback, useEffect, useRef, useState } from "react";
import { type NumberFormatValues } from "react-number-format";
import { type CustomCellEditorProps, useGridCellEditor } from "ag-grid-react";

import { AbbreviationNumericFormat } from "@vega-core/components/Form/Controls/Input/MoneyInput/AbbreviationNumericFormat";
import { type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import {
  getCurrencySymbol,
  useDefaultCurrencySign,
} from "@vega-core/utils/formatMoney";

type MoneyAbbreviationCellEditorParams = CustomCellEditorProps<
  unknown,
  number | null
> & {
  customValidationErrors?: (value: number | null) => string[] | null;
  currency?: CoreCurrency;
};

function getNumericValueFromKey({
  value,
  eventKey,
}: {
  value: number | null | undefined;
  eventKey: string | null;
}): number | null {
  const stored = value ?? null;

  if (eventKey != null && /^\d$/.test(eventKey)) {
    return parseInt(eventKey);
  }

  if (stored !== null) {
    return stored;
  }

  return null;
}

export const MoneyAbbreviationCellEditor = memo(
  ({
    value,
    onValueChange,
    validate,
    cellStartedEdit,
    eventKey,
    customValidationErrors,
    currency,
  }: MoneyAbbreviationCellEditorParams) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [internalValue, setInternalValue] = useState(value ?? null);

    const defaultCurrencySign = useDefaultCurrencySign() ?? "";
    const currencySign = currency
      ? (getCurrencySymbol(currency) ?? currency)
      : defaultCurrencySign;

    const getValidationErrors = useCallback(
      () => customValidationErrors?.(internalValue) ?? null,
      [customValidationErrors, internalValue],
    );

    const getValidationElement = useCallback(() => inputRef.current!, []);

    useGridCellEditor({
      getValidationErrors,
      getValidationElement,
    });

    useEffect(() => {
      inputRef.current?.focus();
      inputRef.current?.select();

      if (cellStartedEdit && eventKey?.length === 1) {
        setInternalValue(getNumericValueFromKey({ value, eventKey }));
      }
    }, []);

    const handleNumericFormatChange = (values: NumberFormatValues) => {
      const numeric = values.floatValue;

      if (numeric !== undefined) {
        setInternalValue(numeric);
        onValueChange(numeric);
      }
    };

    const onBlur = () => {
      validate();
    };

    return (
      <AbbreviationNumericFormat
        className="w-full h-full px-2 bg-transparent border-0 focus:outline-none text-sm"
        isAllowed={(values) => {
          const { value } = values;

          return value.length < 16;
        }}
        onBlur={onBlur}
        onValueChange={handleNumericFormatChange}
        placeholder={m.placeholders_inputMoney({
          sign: currencySign,
        })}
        prefix={m.prefix_inputMoney({
          sign: currencySign,
        })}
        ref={inputRef}
        thousandSeparator=","
        value={internalValue ?? ""}
      />
    );
  },
);
